import { useState, useEffect, useMemo } from 'react';
import api, { customersApi } from '../../../services/api';
import { Customer, ProjectListItem, PaginatedResponse } from '../../../types';

export const useProjects = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | ''>('');
  const [data, setData] = useState<PaginatedResponse<ProjectListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const load = async (page?: number) => {
    try {
      setLoading(true);
      const res = await api.get<PaginatedResponse<ProjectListItem>>('/projects', {
        params: { customer_id: selectedCustomer === '' ? undefined : Number(selectedCustomer), page },
      });
      setData(res.data);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const cs = await customersApi.getAll({ status: 'active', per_page: 1000 });
      setCustomers(cs.data);
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredProjects = useMemo(() => {
    if (!data?.data) return [];
    if (!debouncedSearchQuery.trim()) return data.data;

    const query = debouncedSearchQuery.toLowerCase();
    return data.data.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.project_number.toLowerCase().includes(query) ||
        (p.customer?.client_id && p.customer.client_id.toLowerCase().includes(query)) ||
        (p.customer?.item && p.customer.item.toLowerCase().includes(query)) ||
        (p.deal?.name && p.deal.name.toLowerCase().includes(query)) ||
        (p.status && p.status.toLowerCase().includes(query)) ||
        (p.project_description && p.project_description.toLowerCase().includes(query)),
    );
  }, [data, debouncedSearchQuery, customers]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    customers,
    selectedCustomer,
    setSelectedCustomer,
    data,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    filteredProjects,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    currentProjects,
    handlePageChange,
    load,
  };
};
